import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { create } from 'zustand';
import { documentsApi } from '../api';
import { t } from '../i18n';
import { errorText } from '../lib/errors';
import { toast } from '../stores/toast';
import { Button, ErrorState, Loading, Modal, RadioList, Textarea } from './ui';

/** Пункт чекліста справи, до якого належить документ: тоді у вікні можна одразу прийняти чи відхилити. */
export interface ViewerCheck {
  itemId: string;
  caseId: string;
  status: string;
}

interface ViewerState {
  docId: string | null;
  title: string;
  check: ViewerCheck | null;
  open: (docId: string, title?: string, check?: ViewerCheck | null) => void;
  close: () => void;
}

/** Перегляд документа будь-де в CRM: `openDocument(id, назва)`. */
export const useDocViewer = create<ViewerState>((set) => ({
  docId: null,
  title: '',
  check: null,
  open: (docId, title = '', check = null) => set({ docId, title, check }),
  close: () => set({ docId: null, title: '', check: null }),
}));

export const openDocument = (docId: string, title?: string, check?: ViewerCheck | null) => useDocViewer.getState().open(docId, title, check);

type RejectReason = 'unreadable' | 'wrongDoc' | 'needsTranslation' | 'other';
const REASONS: RejectReason[] = ['unreadable', 'wrongDoc', 'needsTranslation', 'other'];

/**
 * «Що не так з документом?» — причина відхилення обовʼязкова: її побачить клієнт у кабінеті поруч із
 * «Завантажити ще раз». Швидкий вибір + за потреби кілька слів від себе.
 */
export function RejectDialog({
  open,
  name,
  busy,
  onClose,
  onReject,
}: {
  open: boolean;
  name: string;
  busy?: boolean;
  onClose: () => void;
  onReject: (note: string) => void;
}) {
  const [reason, setReason] = useState<RejectReason | ''>('');
  const [text, setText] = useState('');
  const extra = text.trim();
  const note = reason === 'other' ? extra : reason ? [t(`docs.reason.${reason}`), extra].filter(Boolean).join('. ') : '';
  const close = () => {
    setReason('');
    setText('');
    onClose();
  };
  return (
    <Modal
      open={open}
      onClose={close}
      title={t('docs.rejectReason')}
      footer={
        <>
          <Button onClick={close}>{t('common.cancel')}</Button>
          <Button
            variant="solid"
            busy={busy}
            disabled={!note}
            onClick={() => {
              onReject(note);
              setReason('');
              setText('');
            }}
          >
            {t('case.reject')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="m-0 text-sm text-lab">
          {name} · {t('docs.rejectHint')}
        </p>
        <RadioList<RejectReason | ''>
          label={t('docs.rejectReason')}
          value={reason}
          onChange={setReason}
          options={REASONS.map((r) => ({ value: r, label: t(`docs.reason.${r}`) }))}
        />
        {reason !== '' && (
          <Textarea
            label={reason === 'other' ? t('docs.reason.otherText') : t('docs.reason.extraText')}
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={450}
            className="min-h-[72px]"
          />
        )}
      </div>
    </Modal>
  );
}

/** PDF і фото показуємо прямо тут, текст — як текст; решту пропонуємо завантажити. */
export function DocViewer() {
  const { docId, title, check, close } = useDocViewer();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const decide = async (status: 'accepted' | 'rejected', note?: string) => {
    if (!check) return;
    setBusy(true);
    try {
      await documentsApi.setChecklist(check.itemId, status, note);
      setRejecting(false);
      toast.success(status === 'accepted' ? t('viewer.accepted') : t('viewer.rejected'));
      for (const k of [['case', check.caseId], ['cases'], ['pipeline', check.caseId], ['case-events', check.caseId]]) void qc.invalidateQueries({ queryKey: k });
      close();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const q = useQuery({
    queryKey: ['doc-view', docId],
    queryFn: () => documentsApi.downloadUrl(docId!, true),
    enabled: !!docId,
    staleTime: 60_000,
    retry: false,
  });
  const text = useQuery({
    queryKey: ['doc-view-text', docId],
    queryFn: async () => {
      const r = await fetch(q.data!.url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.text();
    },
    enabled: !!q.data?.inline && !!q.data?.mime?.startsWith('text/'),
    retry: false,
  });
  // iframe не повідомляє про помилку завантаження (показує порожній білий прямокутник), тому
  // перед показом PDF перевіряємо, що сховище віддає файл: запит першого байта.
  // Підписане посилання дійсне лише для GET, тому не HEAD, а GET з Range.
  const probe = useQuery({
    queryKey: ['doc-view-probe', docId, q.data?.url],
    queryFn: async () => {
      const r = await fetch(q.data!.url, { headers: { Range: 'bytes=0-0' } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return true;
    },
    enabled: !!q.data?.inline && !q.data?.mime?.startsWith('text/') && !q.data?.mime?.startsWith('image/'),
    retry: false,
    staleTime: 60_000,
  });
  const [imgFailed, setImgFailed] = useState<string | null>(null);
  if (!docId) return null;
  const d = q.data;
  const download = async () => {
    // окреме посилання, щоб не покидати CRM
    let r: Awaited<ReturnType<typeof documentsApi.downloadUrl>>;
    try {
      r = await documentsApi.downloadUrl(docId);
    } catch (e) {
      toast.error(errorText(e));
      return;
    }
    const a = document.createElement('a');
    a.href = r.url;
    a.download = r.file_name;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  return (
    <Modal
      open
      wide
      onClose={close}
      title={title || d?.file_name || t('viewer.title')}
      footer={
        <>
          {d?.inline && (
            <a className="btn" href={d.url} target="_blank" rel="noopener noreferrer">
              {t('viewer.newTab')}
            </a>
          )}
          <Button variant={check?.status === 'uploaded' ? 'outline' : 'solid'} onClick={() => void download()}>
            {t('common.download')}
          </Button>
          {check?.status === 'uploaded' && (
            <>
              <Button disabled={busy} onClick={() => setRejecting(true)}>
                {t('case.reject')}
              </Button>
              <Button variant="solid" busy={busy} onClick={() => void decide('accepted')}>
                {t('case.accept')}
              </Button>
            </>
          )}
        </>
      }
    >
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <ErrorState error={q.error} />
      ) : !d!.inline ? (
        <div className="py-10 text-center flex flex-col gap-2 items-center">
          <b className="font-semibold">{d!.file_name}</b>
          <span className="text-sm text-lab">{t('viewer.noPreview')}</span>
        </div>
      ) : (d!.mime.startsWith('image/') && imgFailed === d!.url) || (d!.mime.startsWith('text/') && text.isError) || probe.isError ? (
        <div role="alert" className="py-10 text-center flex flex-col gap-2 items-center">
          <b className="font-semibold">{d!.file_name}</b>
          <span className="text-sm text-lab max-w-[420px]">{t('viewer.unavailable')}</span>
        </div>
      ) : d!.mime.startsWith('image/') ? (
        <img src={d!.url} alt={d!.file_name} onError={() => setImgFailed(d!.url)} className="max-w-full max-h-[70vh] mx-auto block" />
      ) : d!.mime.startsWith('text/') ? (
        text.isPending ? <Loading /> : <pre className="m-0 max-h-[70vh] overflow-auto whitespace-pre-wrap text-sm bg-tile p-4 rounded">{text.data ?? ''}</pre>
      ) : probe.isPending ? (
        <Loading />
      ) : (
        <iframe title={d!.file_name} src={d!.url} onError={() => void probe.refetch()} className="w-full h-[70vh] border border-line rounded bg-bg" />
      )}
      <RejectDialog
        open={rejecting}
        name={title || d?.file_name || ''}
        busy={busy}
        onClose={() => setRejecting(false)}
        onReject={(note) => void decide('rejected', note)}
      />
    </Modal>
  );
}
